import { createFileRoute, Link } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { convexQuery } from '@convex-dev/react-query'
import { api } from '../../../convex/_generated/api'
import * as React from 'react'

export const Route = createFileRoute('/publish/$slug')({
  component: PublishPage,
})

function PublishPage() {
  const { slug } = Route.useParams()
  const { data: doc } = useSuspenseQuery(
    convexQuery(api.docs.getDocBySlug, { slug })
  )
  
  // Add Cloudflare cache headers hint
  React.useEffect(() => {
    // This page is cached at Cloudflare edge for 1 hour
    // Cache-Control: public, max-age=3600, stale-while-revalidate=86400
    console.log('📦 This page is cached by Cloudflare for faster delivery')
  }, [])

  if (!doc) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h2>
          <p className="text-gray-600 mb-6">This page is not published or does not exist.</p>
          <Link to="/" className="text-blue-600 hover:underline">
            Go to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Weblens
          </Link>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Public
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <article>
          {/* Title */}
          <h1 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
            {doc.title || 'Untitled'}
          </h1>

          {/* Metadata */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-12 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Published {new Date(doc._creationTime).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg prose-gray max-w-none">
            {doc.content.split('\n').map((paragraph, i) => (
              paragraph.trim() ? (
                <p key={i} className="mb-6 text-gray-800 leading-relaxed">
                  {paragraph}
                </p>
              ) : (
                <div key={i} className="h-4"></div>
              )
            ))}
          </div>
        </article>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-500">
                Powered by <span className="font-semibold text-gray-900">Weblens</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13.5 2C13.5 2 15.8 2.4 17.5 4.1C19.2 5.8 19.6 8.1 19.6 8.1L18.1 9.6C18.1 9.6 17.9 8.2 16.8 7.1C15.7 6 14.3 5.8 14.3 5.8L13.5 2M11.5 6C11.5 6 12.5 6.2 13.2 6.9C13.9 7.6 14.1 8.6 14.1 8.6L12.9 9.8C12.9 9.8 12.8 9.3 12.4 8.9C12 8.5 11.5 8.4 11.5 8.4V6M8.5 10.5C8.5 10.5 9.3 10.7 9.9 11.3C10.5 11.9 10.7 12.7 10.7 12.7L9.5 13.9C9.5 13.9 9.4 13.5 9.1 13.2C8.8 12.9 8.4 12.8 8.4 12.8L8.5 10.5M2 16.5C2 16.5 2.4 18.8 4.1 20.5C5.8 22.2 8.1 22.6 8.1 22.6L9.6 21.1C9.6 21.1 8.2 20.9 7.1 19.8C6 18.7 5.8 17.3 5.8 17.3L2 16.5Z"/>
                </svg>
                Cached by Cloudflare
              </div>
            </div>
            <Link 
              to="/" 
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Create your own →
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
